import { BreadCrumb } from "primereact/breadcrumb";
import { Card } from "primereact/card";
import type { MenuItem } from "primereact/menuitem";
import Table from "../components/table";
import { TABLE_COLUMNS_CONFIG, type ColumnMeta } from "../constants/table.cnst";
import { useCallback, useState } from "react";
import { Tag } from "primereact/tag";
import { getSeverity } from "../services/util.service";
import { fetchApi } from "../services/http.service";
import { APIMapping } from "../constants/api-mapping.cnst";
import { HBL_PA_RESOURCES } from "../constants/data.cnst";
import { useQuery } from "@tanstack/react-query";
import type { StationHealthNew, ZoneOption } from "../interfaces/dashboard"
import { MultiSelect } from "primereact/multiselect";
import { useNavigate } from "react-router-dom";
import { DEFAULT_ROUTE_PRIORITY } from "../constants/defaultRoutes";

export default function StationHealthNew() {

    const breadcrumb: MenuItem[] = [
        { label: 'Health', style: { pointerEvents: 'none', fontWeight: 500 } },
        { label: 'Station Health', style: { pointerEvents: 'none' }, className: 'font-semibold text-gray-900' }
    ];
    const stationHealthTableColumns: ColumnMeta[] = TABLE_COLUMNS_CONFIG.STATION_HEALTH_NEW;

    const [stationHealthQueryParams, setStationHealthQueryParams] = useState({
        total_entry_per_page: 10,
        page_num: 1,
    });
    const navigate = useNavigate();


    const [selectedZones, setSelectedZones] = useState<string[]>([]);
    const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
    const [selectedStations, setSelectedStations] = useState<string[]>([]);
    const [expandAllRows, setExpandAllRows] = useState(true);
    const [expansionRequest, setExpansionRequest] = useState(0);
    const [allRowsExpanded, setAllRowsExpanded] = useState(true);
    const [allRowsCollapsed, setAllRowsCollapsed] = useState(false);
    const handleExpansionStateChange = useCallback((allExpanded: boolean, allCollapsed: boolean) => {
        setAllRowsExpanded(allExpanded);
        setAllRowsCollapsed(allCollapsed);
    }, []);
    const { data: filtersResponseData } = useQuery({
        queryKey: ["staion-health-filter"],
        queryFn: () =>
            fetchApi({
                endpoint: APIMapping.GET_ZONES_PROJECT_STATION_IN_STATION_HEALTH,
                resourceName: HBL_PA_RESOURCES.STATION_HEALTH
            }),
        enabled: true
    });

    const zoneOptions: ZoneOption[] = (
        filtersResponseData?.response ?? []
    ).map((zone: any) => ({
        label: zone.zone,
        value: zone.zone,
        projects: zone.projects ?? []
    }));

    const projectOptions = (
        selectedZones.length
            ? zoneOptions.filter((zone) =>
                selectedZones.includes(zone.value)
            )
            : zoneOptions
    ).flatMap((zone) =>
        zone.projects.map((project: any) => ({
            label: project.project,
            value: project.project,
            stations: project.stations ?? []
        }))
    );

    const stationOptions = projectOptions
        .filter((project: any) =>
            selectedProjects.length
                ? selectedProjects.includes(project.value)
                : true
        )
        .flatMap((project: any) =>
            project.stations.map((station: any) => ({
                label: `${station.stationId} (${station.stationName})`,
                value: `${station.stationId} (${station.stationName})`
            }))
        );

    const { data: eventsApiResponse, isFetching } = useQuery<any>({
        queryKey: ["station-health-table", stationHealthQueryParams, selectedStations, selectedZones, selectedProjects],
        queryFn: () =>
            fetchApi({
                endpoint: APIMapping.GET_STATION_HEALTH_NEW,
                queryParams: { ...stationHealthQueryParams, stationId: selectedStations.map((item: any) => item.split(' (')[0]).join(","), zone: selectedZones.join(","), project: selectedProjects.join(",") },
                resourceName: HBL_PA_RESOURCES.STATION_HEALTH
            }),
        enabled: true
    });

    const [first, setFirst] = useState(0);
    const stationHealthTableData: StationHealthNew[] =
        eventsApiResponse?.response?.stations?.map((rowData: StationHealthNew) => {
            return {
                ...rowData,
                failedHealthModules: rowData.failedModules?.map(
                    (module) =>
                        `${module.category.replaceAll('_', ' ')} ${module.count}`
                ) ?? [],
            };
        }) ?? [];
    const [rows, setRows] = useState(10);
    const totalEvents = eventsApiResponse?.response?.totalRecords ?? 0;

    const buildFinalPath = (stationData: any, healthModule: any) => {
        const params = new URLSearchParams(location.search);

        params.set("stationId", `${stationData.stationId} (${stationData.stationName})`);
        params.set("fromDate", stationData['date']);
        params.set("toDate", stationData['date']);

        const path = DEFAULT_ROUTE_PRIORITY.find(r => r.name === healthModule && r.path.includes('station-health'));

        navigate(`${path?.path}?${params.toString()}`);
    };

    const renderStationHealthExpansion = (
        rowData: StationHealthNew
    ) => {
        const stationData = rowData;
        return (
            <div className="p-3">

                <Table
                    id="loco-health-expansion"
                    columns={TABLE_COLUMNS_CONFIG.LOCO_HEALTH_EXPANSION}
                    data={rowData.failedModules ?? []}
                    showGridlines={true}
                    paginator={false}
                    first={0}
                    isHighlight={true}
                    rows={rowData.failedModules?.length ?? 0}
                    enableScroll={false}

                    renderCell={(rowData, field) => {

                        if (field === 'failureDetails') {
                            return (
                                <div className="flex flex-wrap gap-2">
                                    {rowData.failureDetails?.map((issue: any) => {

                                        return (
                                            <Tag
                                                key={issue}
                                                value={issue}
                                                severity="info"
                                            />
                                        );
                                    })}
                                </div>
                            );
                        }

                        if (field === 'moduleName') {
                            return (
                                <a id={`row-${field}`} className="underline cursor-pointer text-brand-600 dark:text-brand-50" onClick={() => buildFinalPath(stationData, rowData[field])}>
                                    {rowData[field]}
                                </a>
                            );
                        }

                        return rowData[field];
                    }}
                />

            </div>
        );
    };

    //     const updateZoneSelection = (selectedZones: string[]) => {
    //     const availableProjects = (filtersResponseData?.response ?? [])
    //         .filter((zone: any) => selectedZones.includes(zone.zone))
    //         .flatMap((zone: any) => zone.projects ?? []);

    //     const validProjectNames = availableProjects.map(
    //         (project: any) => project.project
    //     );

    //     const validProjects = selectedProjects.filter(
    //         (project: string) => validProjectNames.includes(project)
    //     );

    //     const validStationIds = availableProjects
    //         .filter((project: any) => validProjects.includes(project.project))
    //         .flatMap((project: any) => project.stations ?? [])
    //         .map(
    //             (station: any) => `${station.stationId} (${station.stationName})`
    //         );

    //     setSelectedZones(selectedZones);
    //     setSelectedProjects(validProjects);
    //     setSelectedStations((prev: string[]) =>
    //         prev.filter((station) => validStationIds.includes(station))
    //     );


    // };

    // const updateProjectSelection = (selectedProjects: string[]) => {
    //     const validStationIds = (filtersResponseData?.response ?? [])
    //         .filter((zone: any) => selectedZones.includes(zone.zone))
    //         .flatMap((zone: any) => zone.projects ?? [])
    //         .filter((project: any) => selectedProjects.includes(project.project))
    //         .flatMap((project: any) => project.stations ?? [])
    //         .map(
    //             (station: any) => `${station.stationId} (${station.stationName})`
    //         );

    //     setSelectedProjects(selectedProjects);
    //     setSelectedStations((prev: string[]) =>
    //         prev.filter((station) => validStationIds.includes(station))
    //     );


    // };

    const cardHeader = (

        <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
                <h3 className="m-0 font-semibold">Station Health</h3>
                <button
                    type="button"
                    aria-label="Expand all rows"
                    title="Expand all rows"
                    disabled={allRowsExpanded}
                    className="p-2 hover:bg-gray-100 rounded cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                    onClick={() => {
                        setExpandAllRows(true);
                        setExpansionRequest((request) => request + 1);
                    }}
                >
                    <i className="pi pi-plus-circle" />
                </button>
                <button
                    type="button"
                    aria-label="Collapse all rows"
                    title="Collapse all rows"
                    disabled={allRowsCollapsed}
                    className="p-2 hover:bg-gray-100 rounded cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                    onClick={() => {
                        setExpandAllRows(false);
                        setExpansionRequest((request) => request + 1);
                    }}
                >
                    <i className="pi pi-minus-circle" />
                </button>
            </div>


            <div className="flex justify-end gap-2">
                <MultiSelect
                    value={selectedZones}
                    options={zoneOptions ?? []} // [{ label, value }]
                    filter
                    resetFilterOnHide
                    onChange={(e: any) => {
                        const selected = e.value;
                        setSelectedZones(selected);
                        setSelectedProjects([]);
                        setSelectedStations([]);

                        setFirst(0);
                        setRows(10);
                        setStationHealthQueryParams((prev: any) => ({ ...prev, page_num: 1, total_entry_per_page: 10 }));
                    }}
                    placeholder="Select Zones"
                    className="w-56"
                    display="chip"
                />

                <MultiSelect
                    value={selectedProjects}
                    options={projectOptions ?? []} // [{ label, value }]
                    filter
                    resetFilterOnHide
                    onChange={(e: any) => {
                        const selected = e.value;
                        setSelectedProjects(selected);
                        setSelectedStations([]);

                        setFirst(0);
                        setRows(10);
                        setStationHealthQueryParams((prev: any) => ({ ...prev, page_num: 1, total_entry_per_page: 10 }));
                    }}
                    placeholder="Select Projects"
                    className="w-56"
                    display="chip"
                />

                <MultiSelect
                    value={selectedStations}
                    options={stationOptions ?? []} // [{ label, value }]
                    filter
                    resetFilterOnHide
                    onChange={(e: any) => {
                        const selected = e.value;
                        setSelectedStations(selected);

                        setFirst(0);
                        setRows(10);
                        setStationHealthQueryParams((prev: any) => ({ ...prev, page_num: 1, total_entry_per_page: 10 }));
                    }}
                    placeholder="Select Stations"
                    className="w-74"
                    display="chip"
                />
            </div>
        </div>

    );


    return (
        <div className="h-[calc(100vh-4rem)] p-6 flex-1 overflow-y-auto">

            <BreadCrumb model={breadcrumb} />
            <div className="grid grid-cols-1 mb-6 pt-4">

                <Card header={cardHeader}>

                    <Table
                        columns={stationHealthTableColumns}
                        data={stationHealthTableData}
                        showGridlines={true}
                        paginator={true}
                        isLoading={isFetching}
                        first={first}
                        rows={rows}
                        totalRecords={totalEvents}
                        renderCell={(rowData, field, rowIndex, path, expansion) => {

                            if (field === 'serial') {
                                return first + rowIndex + 1;
                            }

                            if (field === 'failedHealthModules') {
                                console.log(rowIndex, path)
                                return (
                                    <div className="flex flex-wrap gap-2">
                                        {rowData.failedHealthModules?.map((issue: any) => {

                                            return (
                                                <Tag
                                                    key={issue}
                                                    value={issue}
                                                    severity={getSeverity(issue)}
                                                />
                                            );
                                        })}
                                    </div>
                                );
                            }


                            if (field === "action") {
                                return (
                                    <button
                                        type="button"
                                        className="p-2 hover:bg-gray-100 rounded cursor-pointer"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            expansion?.toggle();
                                        }}
                                    >
                                        <i
                                            className={
                                                expansion?.isExpanded
                                                    ? "pi pi-chevron-up"
                                                    : "pi pi-chevron-down"
                                            }
                                        />
                                    </button>
                                );
                            }


                            return rowData[field];
                        }}
                        rowExpansion={{
                            key: 'stationId',
                            render: renderStationHealthExpansion
                        }}
                        expandAllRows={expandAllRows}
                        expansionRequest={expansionRequest}
                        onExpansionStateChange={handleExpansionStateChange}
                        onPageChangeExternal={(page_num, total_entry_per_page) => {
                            setFirst((page_num - 1) * total_entry_per_page);
                            setRows(total_entry_per_page);
                            setStationHealthQueryParams((prev: any) => ({
                                ...prev,
                                page_num: page_num,
                                total_entry_per_page: total_entry_per_page
                            }));


                        }}
                    />
                </Card>
            </div>
        </div>
    );

}